import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, Validators} from '@angular/forms';
import {TermsOfUseComponent} from '../terms-of-use/terms-of-use.component';
import {RegistrationService} from '../../services/registration.service';
import {trigger} from '@angular/animations';
import {smoothEnterLeaveAnimation} from '../../../shared/animations/custom-animations';
import {NgbCalendar, NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import * as ValidationPatterns from '../../../shared/validation/custom-validation-patterns.const';
import CustomValidators from '../../../shared/validation/custom-validators';
import {UsernameAsyncValidator} from '../../validation/username-async-validator';
import {Router} from '@angular/router';
import {
  SSN_ALREADY_IN_USE_VALIDATION_ERROR_CODE,
  UNIQUE_VALIDATION_ERROR_CODE,
  NAME_IN_PASSWORD_ERROR_CODE,
  ValidationErrorResponse,
  SSN_FORBIDDEN_ERROR_CODE
} from '../../../shared/models/validation-error-response';
import {DomainNameParserService} from 'src/main/resources/static/angular2/app/shared/services/domain-name-parser.service';
import {PHONE_NUMBER_LIST} from '../../../shared/constants/phone-numbers.const';

@Component({
  selector: 'app-registration-form',
  templateUrl: './registration-form.component.html',
  styleUrls: [
    './registration-form.component.css',
    './../../../../assets/css/floating-labels.css'
  ],
  animations: [
    trigger('openErrorAlert', smoothEnterLeaveAnimation('100ms', '100ms'))
  ]
})
export class RegistrationFormComponent implements OnInit {
  

  memberAlreadyRegisteredMessageShown = false;
  genericErrorMessageShown = false;
  passwordContainsNameErrorMessageShown = false;
  termsOfUseErrorMessageShown = false;
  ssnRequiredMessageShown = false;
  ssnRequiredLengthMessageShown = false;
  ssnForbiddenSymbolsMessageShown = false;
  surnameRequiredMessageShown = false;
  surnameMaxLengthMessageShown = false;
  surnameCannotContainCharsMessageShown = false;
  givenNameRequiredMessageShown = false;
  givenNameMinLengthMessageShown = false;
  givenNameMaxLengthMessageShown = false;
  givenNameNotTrimmedMessageShown = false;
  dobFormatMessageShown = false;
  phoneFormatMessageShown = false;
  errorAlertWindowOpened = false;
  submitted = false;
  firstDateOfCurrentYear = new Date(new Date().getFullYear(), 0, 0);
  daysThisYearSoFar = Math.floor((Date.now() - Date.parse(this.firstDateOfCurrentYear.toDateString()))/24/60/60/1000);
  maxDatepickerDate = this.calendar.getPrev(this.calendar.getToday(), 'd', this.daysThisYearSoFar);
  minDatepickerDate = this.calendar.getPrev(this.calendar.getToday(), 'y', 120);
  customerServicePhone: string;
  unmaskedSsn: string;

  @ViewChild('dateOfBirth') dateOfBirth: ElementRef;

  regForm = new FormGroup({
    usernameInput: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(7),
        Validators.maxLength(100),
        Validators.pattern(ValidationPatterns.ALPHANUMERIC_SYMBOLS_PATTERN)
      ],
      asyncValidators: [this.usernameAsyncValidator.validate.bind(this.usernameAsyncValidator)]
    }),
    passwordInput: new FormControl('', {
      validators: [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(40),
        Validators.pattern(ValidationPatterns.PASSWORD_VALIDATION_PATTERN),
        CustomValidators.atLeastOneLowerCaseCharacterValidator,
        CustomValidators.atLeastOneUpperCaseCharacterValidator,
        CustomValidators.atLeastOneDigitValidator,
        CustomValidators.atLeastOneSpecialCharacterValidator,
        CustomValidators.repeatingCharsValidator,
        CustomValidators.consecutiveCharsValidator
      ],
      asyncValidators: [this.usernameAsyncValidator.validatePasswordErrors.bind(this.usernameAsyncValidator)]
    }),
    confirmPasswordInput: new FormControl('', [Validators.required]),
    firstNameInput: new FormControl('', [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(64),
      Validators.pattern(ValidationPatterns.NAME_VALIDATION_PATTERN)
    ]),
    lastNameInput: new FormControl('', [
      Validators.required,
      Validators.minLength(1),
      Validators.maxLength(64),
      Validators.pattern(ValidationPatterns.NAME_VALIDATION_PATTERN)
    ]),
    ssnInput: new FormControl('', [
      Validators.required,
      Validators.pattern(ValidationPatterns.SSN_VALIDATION_PATTERN),
      CustomValidators.ssnLengthValidator
    ]),
    dobInput: new FormControl('', [
      Validators.required,
      CustomValidators.dobNotFutureValidator(this.maxDatepickerDate),
      CustomValidators.dobAfterMinDate(this.minDatepickerDate)
    ]),
    emailInput: new FormControl('', [
      Validators.required,
      Validators.email
    ]),
    confirmEmailInput: new FormControl('', [Validators.required]),
    phoneNumberInput: new FormControl('', [
      Validators.pattern(ValidationPatterns.PHONE_VALIDATION_PATTERN)
    ])
  }, {
    validators: [CustomValidators.getConfirmPasswordValidator, CustomValidators.getConfirmEmailValidator]
  });

  constructor(private calendar: NgbCalendar,
              private registrationService: RegistrationService,
              private usernameAsyncValidator: UsernameAsyncValidator,
              private router: Router,
              private domainNameParserService: DomainNameParserService,
              private ngbDateParserFormatter: NgbDateParserFormatter,
              private el: ElementRef) {
    this.customerServicePhone = PHONE_NUMBER_LIST[this.domainNameParserService.getState()];
  }

  ngOnInit(): void {
  }

  get formControls(): { [p: string]: AbstractControl } {
    return this.regForm.controls;
  }

  public getMinDateFormatted(): string {
    return this.ngbDateParserFormatter.format(this.minDatepickerDate);
  }

  public submitRegistrationForm(tou: TermsOfUseComponent): void {
    this.submitted = true;

    console.log('this.regForm.invalid: ' + this.regForm.invalid);
    console.log('this.regForm: ' +  JSON.stringify(this.regForm.value));

    // stop here if form is invalid
    if (this.regForm.invalid || !tou.checkboxFlag || !tou?.currentTermsOfUseResponse) {
      this.focusOnFirstInputWithError();
      return;
    }

    console.log('regForm: ' + JSON.stringify(this.regForm.value));
    console.log('tou=' + tou.checkboxFlag);

    this.registrationService.registerUser(this.regForm.value, tou?.currentTermsOfUseResponse)
      .subscribe(validationErrorResponse => {
        if (validationErrorResponse == null) {
          // show success page
          this.router.navigate(['/confirm-email'], {state: {username: this.regForm.value.usernameInput}});
        } else {
          this.handleValidationErrorResponse(validationErrorResponse);
        }
    }, error => {
        console.error('ServiceLayer returned error. Details: ' + JSON.stringify(error));
        // show generic error message
        this.showGenericErrorMessage();
    });
  }

  private scrollToTop(): void {
    window.scrollTo(0, 0);
  }

  private focusOnFirstInputWithError(): void {
    for (const key of Object.keys(this.regForm.controls)) {
      if (this.regForm.controls[key].invalid) {
        const invalidControl = this.el.nativeElement.querySelector('[formcontrolname="' + key + '"]');
        invalidControl.focus();
        invalidControl.blur();
        break;
      }
    }
  }

  private handleValidationErrorResponse(validationErrorResponse: ValidationErrorResponse): void {
    if (this.containsSSNAlreadyInUseValidationError(validationErrorResponse) ||
      this.containsUniqueValidationError(validationErrorResponse)) {
      // show an error with already registered message
      this.showMemberAlreadyRegisteredMessage();
    } else if (this.containsNameInPasswordError(validationErrorResponse)) {
      // show an error with name-in-password message
      this.showNameInPasswordErrorMessage();
    } else if (this.containsSsnForbiddenSymbolsError(validationErrorResponse)) {
      // show an error with forbidden-symbols in ssn message
      this.showSsnForbiddenSymbolsMessage();
    } else if (this.containsSsnRequiredError(validationErrorResponse)) {
      // show an error with ssn empty message
      this.showSsnRequiredMessage();
    } else if (this.containsSsnRequiredLengthError(validationErrorResponse)) {
      // show an error with ssn-does-not-meet required-length message
      this.showSsnRequiredLengthMessage();
    } else if (this.containsSurnameRequiredError(validationErrorResponse)) {
      // show an error with last-name missing message
      this.showSurnameRequiredMessage();
    } else if (this.containsSurnameMaxLengthError(validationErrorResponse)) {
      // show an error with last-name max-length message
      this.showSurnameMaxLengthMessage();
    } else if (this.containsSurnameCannotContainCharsError(validationErrorResponse)) {
      // show an error with last-name contains special chars message
      this.showSurnameCannotContainCharsMessage();
    } else if (this.containsGivenNameRequiredError(validationErrorResponse)) {
      // show an error with first-name missing message
      this.showGivenNameRequiredMessage();
    } else if (this.containsGivenNameMinLengthError(validationErrorResponse)) {
      // show an error with first-name min-length message
      this.showGivenNameMinLengthMessage();
    } else if (this.containsGivenNameMaxLengthError(validationErrorResponse)) {
      // show an error with first-name max-length message
      this.showGivenNameMaxLengthMessage();
    } else if (this.containsGivenNameNotTrimmedError(validationErrorResponse)) {
      // show an error with first-name not trimmed message
      this.showGivenNameNotTrimmedMessage();
    } else if (this.containsDobFormatError(validationErrorResponse)) {
      // show an error with date-of-birth format message
      this.showDobFormatMessage();
    } else if (this.containsPhoneFormatError(validationErrorResponse)) {
      // show an error with phone-number format message
      this.showPhoneFormatMessage();
    } else {
      console.error('ServiceLayer registration endpoint returned validation error. ' +
        'Details: ' + JSON.stringify(validationErrorResponse));
      // show generic error message
      this.showGenericErrorMessage();
    }
  }

  private showGenericErrorMessage(): void {
    this.resetErrorsShown();
    this.genericErrorMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showNameInPasswordErrorMessage(): void {
    this.resetErrorsShown();
    this.passwordContainsNameErrorMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showMemberAlreadyRegisteredMessage(): void {
    this.resetErrorsShown();
    this.memberAlreadyRegisteredMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showSsnForbiddenSymbolsMessage(): void {
    this.resetErrorsShown();
    this.ssnForbiddenSymbolsMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showSsnRequiredMessage(): void {
    this.resetErrorsShown();
    this.ssnRequiredMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showSsnRequiredLengthMessage(): void {
    this.resetErrorsShown();
    this.ssnRequiredLengthMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showSurnameRequiredMessage(): void {
    this.resetErrorsShown();
    this.surnameRequiredMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showSurnameMaxLengthMessage(): void {
    this.resetErrorsShown();
    this.surnameMaxLengthMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showSurnameCannotContainCharsMessage(): void {
    this.resetErrorsShown();
    this.surnameCannotContainCharsMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showGivenNameRequiredMessage(): void {
    this.resetErrorsShown();
    this.givenNameRequiredMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showGivenNameMinLengthMessage(): void {
    this.resetErrorsShown();
    this.givenNameMinLengthMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showGivenNameMaxLengthMessage(): void {
    this.resetErrorsShown();
    this.givenNameMaxLengthMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showGivenNameNotTrimmedMessage(): void {
    this.resetErrorsShown();
    this.givenNameNotTrimmedMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showDobFormatMessage(): void {
    this.resetErrorsShown();
    this.dobFormatMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private showPhoneFormatMessage(): void {
    this.resetErrorsShown();
    this.phoneFormatMessageShown = true;
    this.errorAlertWindowOpened = true;
    this.scrollToTop();
  }

  private resetErrorsShown(): void {
    this.passwordContainsNameErrorMessageShown = false;
    this.memberAlreadyRegisteredMessageShown = false;
    this.genericErrorMessageShown = false;
    this.errorAlertWindowOpened = false;
  }

  private containsUniqueValidationError(validationResponse: ValidationErrorResponse): boolean {
    return this.containsValidationError(validationResponse, UNIQUE_VALIDATION_ERROR_CODE);
  }

  private containsSSNAlreadyInUseValidationError(validationResponse: ValidationErrorResponse): boolean {
    return this.containsValidationError(validationResponse, SSN_ALREADY_IN_USE_VALIDATION_ERROR_CODE);
  }

  private containsNameInPasswordError(validationResponse: ValidationErrorResponse): boolean {
    return this.containsValidationError(validationResponse, NAME_IN_PASSWORD_ERROR_CODE);
  }

  private containsSsnForbiddenSymbolsError(validationResponse: ValidationErrorResponse): boolean {
    return this.containsValidationError(validationResponse, SSN_FORBIDDEN_ERROR_CODE);
  }

  private containsSsnRequiredError(validationResponse: ValidationErrorResponse) {
    return this.containsPolicyError(validationResponse, "ssn", "REQUIRED");
  }

  private containsSsnRequiredLengthError(validationResponse: ValidationErrorResponse) {
    return this.containsPolicyError(validationResponse, "ssn", "MAX_LENGTH");
  }

  private containsSurnameRequiredError(validationResponse: ValidationErrorResponse) {
    return this.containsPolicyError(validationResponse, "sn", "REQUIRED");
  }

  private containsSurnameMaxLengthError(validationResponse: ValidationErrorResponse) {
    return this.containsPolicyError(validationResponse, "sn", "MAX_LENGTH");
  }

  private containsSurnameCannotContainCharsError(validationResponse: ValidationErrorResponse) {
    return this.containsPolicyError(validationResponse, "sn", "CANNOT_CONTAIN_CHARACTERS");
  }

  private containsGivenNameRequiredError(validationResponse: ValidationErrorResponse) {
    return this.containsPolicyError(validationResponse, "givenName", "REQUIRED");
  }

  private containsGivenNameMinLengthError(validationResponse: ValidationErrorResponse) {
    return this.containsPolicyError(validationResponse, "givenName", "MIN_LENGTH");
  }

  private containsGivenNameMaxLengthError(validationResponse: ValidationErrorResponse) {
    return this.containsPolicyError(validationResponse, "givenName", "MAX_LENGTH");
  }

  private containsGivenNameNotTrimmedError(validationResponse: ValidationErrorResponse) {
    return this.containsPolicyError(validationResponse, "givenName", "NOT_TRIMMED");
  }

  private containsDobFormatError(validationResponse: ValidationErrorResponse) {
    return this.containsPolicyError(validationResponse, "dearbornDOB", "DATE_FORMAT");
  }

  private containsPhoneFormatError(validationResponse: ValidationErrorResponse) {
    return this.containsPolicyError(validationResponse, "mobile", "VALID_PHONE_FORMAT");
  }

  private containsValidationError(validationResponse: ValidationErrorResponse, errorCode: string): boolean {
    for (const validationError of validationResponse?.errors) {
      if (validationError.code === errorCode) {
        return true;
      }
    }
    return false;
  }

  private containsPolicyError(validationResponse: ValidationErrorResponse, errorField: string, errorCode: string): boolean {
    for (const validationError of validationResponse?.errors) {
      if (validationError.field === errorField && validationError.code === errorCode) {
        return true;
      }
    }
    return false;
  }

  maskSSNOnBlur(field) {
    this.unmaskedSsn = field.value;
    if (field.value.length === 11) {
      field.value = 'XXX-XX-' + this.unmaskedSsn.slice(7);
    }
  }

  showSSNOnFocus(field) {
    if (this.unmaskedSsn == null) {
      field.value = '';
    } else {
      field.value = this.unmaskedSsn;
    }
  }

  allowOnlyLetters(field) {
    const inputCode = field.code.charCodeAt();
    const inputKey = field.key.charCodeAt();
    /** Prevent any key that is not:
     *  - Letters (inputCode = 75)
     *  - Space (inputKey = 32)
     *  - Apostrophe (inputKey = 39)
     *  - Hyphen (inputKey = 45)
     *  - Backspace (inputKey = 66)
     *  - Tab (inputKey = 84)
     *  - Arrow Key (inputKey = 65)
     */
    if (!(inputCode === 75 || inputKey === 32 || inputKey === 39 || inputKey === 45 || inputKey === 66 || inputKey === 84 || inputKey === 65)) {
      field.preventDefault();
    }
  }

  allowOnlyAlphaNumerics(field) {
    const inputCode = field.code.charCodeAt();
    const inputKey = field.key.charCodeAt();
    /** Prevent any key that is not:
     *  - Letters (inputCode = 75)
     *  - Numbers (inputKey >= 48 && <= 57)
     *  - Backspace (inputKey = 66)
     *  - Tab (inputKey = 84)
     *  - Arrow Key (inputKey = 65)
     */
     if (!(inputCode === 75 || (inputKey >= 48 && inputKey <= 57) || inputKey === 66 || inputKey === 84 || inputKey === 65)) {
      field.preventDefault();
    }
  }

  showTermsOfUseErrorMessage(value: boolean) {
    if (value) {
      this.resetErrorsShown();
      this.termsOfUseErrorMessageShown = true;
      this.errorAlertWindowOpened = true;
      this.scrollToTop();
    }
  }
}
